import type { MouseEvent, ReactNode } from 'react';
import { Browser } from '@capacitor/browser';
import { isNativePlatform } from '../native';

const URL_PATTERN = /https?:\/\/[^\s<>"']+/gi;
const TRAILING_PUNCTUATION = new Set(['.', ',', ';', ':', '!', '?', ')', ']']);

// A ")" or "]" that closes an opener inside the URL stays in the href
// (https://en.wikipedia.org/wiki/Iron_(metal)). A trailing ")." does not.
function peelTrailing(raw: string): { href: string; trailing: string } {
  let href = raw;
  let trailing = '';
  while (href.length > 0) {
    const last = href[href.length - 1];
    if (!TRAILING_PUNCTUATION.has(last)) break;
    if (last === ')' && count(href, '(') >= count(href, ')')) break;
    if (last === ']' && count(href, '[') >= count(href, ']')) break;
    trailing = last + trailing;
    href = href.slice(0, -1);
  }
  return { href, trailing };
}

function count(value: string, char: string): number {
  return value.split(char).length - 1;
}

function isLinkable(href: string): boolean {
  return /^https?:\/\/.+/i.test(href);
}

export function openMessageUrl(url: string, native = isNativePlatform) {
  if (native) {
    void Browser.open({ url });
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function renderMessageContent(content: string): ReactNode {
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  for (const match of content.matchAll(URL_PATTERN)) {
    const raw = match[0];
    const index = match.index ?? 0;
    const { href, trailing } = peelTrailing(raw);
    if (index > cursor) nodes.push(content.slice(cursor, index));
    if (isLinkable(href)) {
      nodes.push(
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="underline break-all"
          onClick={(event: MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault();
            openMessageUrl(href);
          }}
        >
          {href}
        </a>,
      );
      key += 1;
      if (trailing) nodes.push(trailing);
    } else {
      nodes.push(raw);
    }
    cursor = index + raw.length;
  }
  if (cursor < content.length) nodes.push(content.slice(cursor));
  return nodes.length > 0 ? nodes : content;
}
